import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import DownloadForm from '../../components/common/DownloadForm'

const AiInHealthcareOperations: React.FC = () => {
  const navigate = useNavigate()
  
  // Reading progress
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [progress, setProgress] = useState(0)
  
  // Active section highlight
  const [activeId, setActiveId] = useState<string>('exec-summary')

  // Download form state
  const [downloadFormOpen, setDownloadFormOpen] = useState(false)

  const sections = [
    { id: "exec-summary", labelKey: "AiInHealthcareOperations.sections.executiveSummary" },
    { id: "ai-beyond-hype", labelKey: "AiInHealthcareOperations.sections.aiBeyondHype" },
    { id: "practical-use-cases", labelKey: "AiInHealthcareOperations.sections.practicalUseCases" },
    { id: "benefits", labelKey: "AiInHealthcareOperations.sections.benefits" },
    { id: "risks-ethics", labelKey: "AiInHealthcareOperations.sections.risksEthics" },
    { id: "roadmap", labelKey: "AiInHealthcareOperations.sections.roadmap" },
    { id: "conclusion", labelKey: "AiInHealthcareOperations.sections.conclusion" },
    { id: "references", labelKey: "AiInHealthcareOperations.sections.references" },
  ]

  useEffect(() => {
    const onScroll = () => {
      const el = contentRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const total = el.scrollHeight - window.innerHeight + rect.top
      const header = document.getElementById('navigation')
      const headerOffset = header ? header.offsetHeight + 16 : 80
      const scrolled = Math.min(
        Math.max(window.scrollY - (el.offsetTop - headerOffset), 0),
        Math.max(total, 1)
      )
      setProgress((scrolled / Math.max(total, 1)) * 100)
    }
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  useEffect(() => {
    const observers: IntersectionObserver[] = []
    sections.forEach(({ id }) => {
      const el = document.getElementById(id)
      if (!el) return
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) setActiveId(id)
          })
        },
        { rootMargin: "-40% 0px -50% 0px", threshold: 0.01 }
      )
      obs.observe(el)
      observers.push(obs)
    })
    return () => observers.forEach((o) => o.disconnect())
  }, [])

  // Smooth scroll to anchors
  const scrollTo = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    // Get actual header height dynamically
    const header = document.getElementById('navigation')
    const headerOffset = header ? header.offsetHeight + 16 : 80 // 16px extra padding
    window.scrollTo({ top: el.offsetTop - headerOffset, behavior: "smooth" })
  }

  // Helper UI component
  const MetaItem = ({ label, value }: { label: string; value: string }) => (
    <div className="flex items-center gap-2 text-sm text-slate-600">
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-slate-400" />
      <span className="font-medium text-slate-700">{label}:</span>
      <span>{value}</span>
    </div>
  )

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Reading progress bar */}
      <div className="fixed inset-x-0 top-0 z-50 h-1 bg-slate-200">
        <div
          className="h-full bg-gradient-to-r from-sky-500 to-indigo-500 transition-[width]"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Hero */}
      <header className="pt-20 pb-10 border-b border-slate-200 bg-[radial-gradient(1200px_600px_at_10%_-10%,#e8f1ff_0%,#f3f7ff_30%,#ffffff_65%)]">
        <div className="mx-auto max-w-6xl px-4">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3 py-1 text-xs font-medium text-slate-700 shadow-sm backdrop-blur">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
            White Paper | AI in Healthcare Operations
          </div>

          <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-slate-900">
            AI in Healthcare Operations: Practical Applications for Smarter Collaboration and Better Care
          </h1>
          <p className="mt-3 max-w-3xl text-slate-700 text-base md:text-lg">
            Exploring practical applications of AI in referral management, scheduling, bed management, staffing, and case prioritization to optimize resources and improve patient flow.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-4">
            <MetaItem label="Author" value="EceptionistCX Research Group" />
            <MetaItem label="Published" value="August 25, 2025" />
            <MetaItem label="Length" value="12 min read" />
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={() => setDownloadFormOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Download PDF
            </button>
            <button
              onClick={() => navigator.share?.({ title: "AI in Healthcare Operations", url: window.location.href })}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-slate-800 shadow-sm hover:bg-slate-50"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
              Share
            </button>
          </div>
        </div>
      </header>

      {/* Content & Sidebar */}
      <main ref={contentRef} className="mx-auto max-w-6xl px-4 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Article */}
        <article className="lg:col-span-8">
          {sections.map((s, i) => (
            <section key={s.id} id={s.id} className={`scroll-mt-20 ${i !== 0 ? "mt-10" : ""}`}>
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
                {s.id === "exec-summary" && "⭐ Executive Summary"}
                {s.id === "ai-beyond-hype" && "🤖 1. AI Beyond the Hype"}
                {s.id === "practical-use-cases" && "📊 2. Practical Use Cases"}
                {s.id === "benefits" && "💡 3. Benefits of AI in Operations"}
                {s.id === "risks-ethics" && "⚠️ 4. Risks and Ethics"}
                {s.id === "roadmap" && "🛠️ 5. Roadmap for Adoption"}
                {s.id === "conclusion" && "✅ 6. Conclusion"}
                {s.id === "references" && "📚 References"}
              </h2>
              <div className="mt-4 prose prose-slate max-w-none">
                {s.id === "exec-summary" && (
                  <>
                    <p>
                      Artificial Intelligence (AI) is transforming healthcare operations by making workflows more efficient, supporting smarter collaboration, and helping providers deliver better care. While clinical AI often dominates headlines, the greatest near-term impact lies in operational AI—using predictive analytics, triage, and automation to optimize resources and improve patient flow.
                    </p>
                    <br />
                    <p>
                      This white paper explores practical applications of AI in referral management, scheduling, bed management, staffing, and case prioritization. It also highlights the financial and operational benefits of predictive analytics, while emphasizing the importance of ethical adoption and human oversight [1][2][3].
                    </p>
                    <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <p className="m-0 text-sm"><span className="font-semibold">Key Insight:</span> Operational AI delivers the most immediate ROI via triage, scheduling, staffing & bed optimization.</p>
                    </div>
                  </>
                )}
                {s.id === "ai-beyond-hype" && (
                  <>
                    <p>
                      AI is often misunderstood in healthcare. In reality, it can be broken into two categories:
                    </p>
                    <br />
                    <ul>
                      <li>- Clinical AI: Tools for diagnosis, imaging, and treatment recommendations.</li>
                      <li>- Operational AI: Tools for workflow automation, triage, and resource optimization.</li>
                    </ul>
                    <br />
                    <p>
                      Operational AI offers the most immediate ROI for health systems by improving efficiency, reducing delays, enhancing patient experience, and supporting capacity planning including bed utilization [1].
                    </p>
                  </>
                )}
                {s.id === "practical-use-cases" && (
                  <>
                    <p>
                      Key operational use cases for AI include:
                    </p>
                    <br />
                    <ul>
                      <li>- Referral triage and urgency classification [2].</li>
                      <li>- Scheduling optimization to maximize slot utilization [2].</li>
                      <li>- Predictive analytics for staffing and bed capacity planning [1][3].</li>
                      <li>- Case prioritization and administrative automation [2].</li>
                    </ul>
                    <br />
                    <p>
                      These use cases are already being deployed in leading health systems to improve throughput and reduce staff burden.
                    </p>
                  </>
                )}
                {s.id === "benefits" && (
                  <>
                    <p>
                      AI delivers measurable benefits to health systems:
                    </p>
                    <br />
                    <ul>
                      <li>- Faster and more accurate decision-making.</li>
                      <li>- Reduced administrative workload for staff.</li>
                      <li>- Improved resource allocation, including staff and bed management [1].</li>
                      <li>- Data capture for continuous improvement.</li>
                    </ul>
                    <br />
                    <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                      <p className="m-0 text-sm"><span className="font-semibold">Impact:</span> According to Frost & Sullivan, predictive analytics could save the U.S. healthcare system up to $70 billion annually [1].</p>
                    </div>
                  </>
                )}
                {s.id === "risks-ethics" && (
                  <>
                    <p>
                      While AI has transformative potential, adoption must be responsible:
                    </p>
                    <br />
                    <ul>
                      <li>- Protecting patient privacy and ensuring HIPAA/GDPR compliance.</li>
                      <li>- Addressing bias and ensuring algorithmic fairness.</li>
                      <li>- Maintaining transparency in decision-making.</li>
                      <li>- Keeping humans in the loop to ensure accountability.</li>
                    </ul>
                    <br />
                    <p>
                      Health systems should adopt AI with clear governance and ethical frameworks in place [2].
                    </p>
                  </>
                )}
                {s.id === "roadmap" && (
                  <>
                    <p>
                      Health systems can adopt AI in a phased approach:
                    </p>
                    <br />
                    <ol>
                      <li>1. Identify one operational workflow (e.g., referral triage or scheduling).</li>
                      <li>2. Pilot AI tools and measure impact.</li>
                      <li>3. Scale adoption across departments.</li>
                      <li>4. Continuously refine models and monitor outcomes.</li>
                    </ol>
                    <br />
                    <p>
                      Partnering with SaaS platforms like EceptionistCX ensures AI is embedded responsibly and effectively [3].
                    </p>
                  </>
                )}
                {s.id === "conclusion" && (
                  <>
                    <p>
                      AI is no longer a future promise—it is a present-day solution for healthcare operations. By deploying AI in referral management, scheduling, staffing, and bed management, health systems can improve efficiency, reduce costs, and deliver better patient care. The key is to adopt responsibly, with transparency and human oversight at the core [1][2][3].
                    </p>
                    <button
                      onClick={() => setDownloadFormOpen(true)}
                      className="mt-3 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      Download Full Paper
                    </button>
                  </>
                )}
                {s.id === "references" && (
                  <>
                    <ol>
                      <li>1. Frost & Sullivan. Predictive Analytics in Healthcare: Market Potential and Cost Savings. 2021.</li>
                      <li>2. PwC Health Research Institute. Top Health Industry Issues. 2022.</li>
                      <li>3. Precedence Research. AI in Healthcare Market Size, 2023.</li>
                    </ol>
                    <div className="mt-6 rounded-xl border border-slate-200 p-4">
                      <h4 className="text-lg font-semibold mb-2">📈 Appendix: Charts and Visuals</h4>
                      <ul className="text-sm text-slate-600 space-y-1">
                        <li>• Projected AI in Healthcare Market (USD B)</li>
                        <li>• Relative Impact of AI Use Cases in Operations</li>
                        <li>• Potential Annual Savings from Predictive Analytics (USD B)</li>
                        <li>• Hospital Bed Occupancy Optimization with AI (%)</li>
                      </ul>
                    </div>

                    {/* Charts and Visuals Images */}
                    <div className="mt-6 space-y-6">
                      <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <h5 className="text-base font-semibold mb-3 text-slate-900">Projected AI in Healthcare Market Growth</h5>
                        <img 
                          src="/Image/WhitePapers/AI_HealthcareOps_WhitePaper/AI_HealthcareOps_WhitePaper_image1.png" 
                          alt="Projected AI in Healthcare Market Growth Chart"
                          className="w-full rounded-lg shadow-sm"
                        />
                      </div>

                      <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <h5 className="text-base font-semibold mb-3 text-slate-900">AI Use Cases Impact Analysis</h5>
                        <img 
                          src="/Image/WhitePapers/AI_HealthcareOps_WhitePaper/AI_HealthcareOps_WhitePaper_image2.png" 
                          alt="AI Use Cases Impact Analysis Chart"
                          className="w-full rounded-lg shadow-sm"
                        />
                      </div>

                      <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <h5 className="text-base font-semibold mb-3 text-slate-900">Predictive Analytics Cost Savings</h5>
                        <img 
                          src="/Image/WhitePapers/AI_HealthcareOps_WhitePaper/AI_HealthcareOps_WhitePaper_image3.png" 
                          alt="Predictive Analytics Cost Savings Chart"
                          className="w-full rounded-lg shadow-sm"
                        />
                      </div>

                      <div className="rounded-xl border border-slate-200 bg-white p-4">
                        <h5 className="text-base font-semibold mb-3 text-slate-900">Hospital Bed Occupancy Optimization</h5>
                        <img 
                          src="/Image/WhitePapers/AI_HealthcareOps_WhitePaper/AI_HealthcareOps_WhitePaper_image4.png" 
                          alt="Hospital Bed Occupancy Optimization Chart"
                          className="w-full rounded-lg shadow-sm"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </section>
          ))}
        </article>

        {/* Sidebar */}
        <aside className="lg:col-span-4 lg:pl-2">
          <div className="lg:sticky lg:top-32 space-y-6">
            {/* Summary card */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Summary</div>
              <p className="mt-2 text-sm text-slate-700">
                This white paper explores how AI transforms healthcare operations through practical applications in referral management, scheduling, and resource optimization.
              </p>
              <ul className="mt-3 space-y-2 text-sm text-slate-700 list-disc pl-5">
                <li>Operational AI delivers immediate ROI</li>
                <li>Focus on triage, scheduling, and bed management</li>
                <li>Ethical adoption with human oversight</li>
                <li>Phased implementation approach</li>
              </ul>
              <button
                onClick={() => setDownloadFormOpen(true)}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Download PDF
              </button>
            </div>

            {/* TOC */}
            <nav className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Table of Contents</div>
              <ul className="mt-3 space-y-2">
                {sections.map((s) => (
                  <li key={s.id}>
                    <button
                      onClick={() => scrollTo(s.id)}
                      className={
                        "text-left text-sm w-full rounded-lg px-2 py-1 hover:bg-slate-50 " +
                        (activeId === s.id ? "font-semibold text-slate-900" : "text-slate-700")
                      }
                    >
                      {s.id === "exec-summary" && "Executive Summary"}
                      {s.id === "ai-beyond-hype" && "AI Beyond the Hype"}
                      {s.id === "practical-use-cases" && "Practical Use Cases"}
                      {s.id === "benefits" && "Benefits of AI in Operations"}
                      {s.id === "risks-ethics" && "Risks and Ethics"}
                      {s.id === "roadmap" && "Roadmap for Adoption"}
                      {s.id === "conclusion" && "Conclusion"}
                      {s.id === "references" && "References"}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Citation */}
            <div id="cite" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Citation</div>
              <p className="mt-2 text-xs text-slate-700">
                EceptionistCX Research Group. "AI in Healthcare Operations: Practical Applications for Smarter Collaboration and Better Care." August 25, 2025.
              </p>
              <button
                onClick={() => {
                  const text = 'EceptionistCX Research Group. "AI in Healthcare Operations: Practical Applications for Smarter Collaboration and Better Care." August 25, 2025.'
                  navigator.clipboard?.writeText(text)
                }}
                className="mt-2 inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs hover:bg-slate-50"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                Copy Citation
              </button>
            </div>
          </div>
        </aside>
      </main>

      {/* Related white papers */}
      <section className="border-t border-slate-200 bg-slate-50 py-10">
        <div className="mx-auto max-w-6xl px-4">
          <h3 className="text-xl md:text-2xl font-semibold text-slate-900">Related White Papers</h3>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { title: "The Future of Referral Management in Healthcare", description: "From fax to AI-powered workflows that eliminate administrative waste and improve access.", route: "/whitepapers/referral-cx" },
              { title: "Digital Front Doors in Healthcare", description: "Why scheduling matters more than ever for patient experience and provider efficiency.", route: "/whitepapers/scheduling-cx" },
              { title: "Breaking Silos with Integrated Care Coordination", description: "How integrated platforms improve outcomes and reduce costs across healthcare systems.", route: "/whitepapers/integrated-care" }
            ].map((card, index: number) => (
              <button
                key={index}
                onClick={() => navigate(card.route)}
                className="group block rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow text-left w-full"
              >
                <div className="flex items-start justify-between gap-4">
                  <h4 className="text-base font-semibold text-slate-900 group-hover:underline">
                    {card.title}
                  </h4>
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-white">→</span>
                </div>
                <p className="mt-2 text-sm text-slate-700">{card.description}</p>
              </button>
            ))}
          </div>
          <div className="mt-6">
            <button 
              onClick={() => navigate('/whitepapers')}
              className="text-sm font-medium text-slate-700 hover:underline"
            >
              ← Back to White Papers
            </button>
          </div>
        </div>
      </section>

      {/* Footer spacer */}
      <div className="h-8" />

      {/* Download Form Modal */}
      <DownloadForm
        isOpen={downloadFormOpen}
        onClose={() => setDownloadFormOpen(false)}
        paperTitle="AI in Healthcare Operations: Practical Applications for Smarter Collaboration and Better Care"
        pdfPath="/PDF/WhitePapers/AI_HealthcareOps_WhitePaper.pdf"
      />
    </div>
  )
}

export default AiInHealthcareOperations
